import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import type { AgentMemoryScope, AgentYamlConfig } from '@/lib/agentYaml'

const labelClass = 'text-xs font-bold leading-[160%] tracking-[0.015em] text-foreground'

const options: { value: AgentMemoryScope; title: string; hint: string }[] = [
  { value: 'project', title: 'Project memory', hint: 'The agent remembers within a project but never carries context across projects.' },
  { value: 'none', title: 'No memory', hint: 'Every run starts from a clean slate.' },
]

interface AgentMemoryScopeFormProps {
  config: AgentYamlConfig
  onChange: (config: AgentYamlConfig) => void
  disabled?: boolean
}

export function AgentMemoryScopeForm({ config, onChange, disabled = false }: AgentMemoryScopeFormProps) {
  return (
    <div className="space-y-2" data-testid="agent-memory-scope-form">
      <Label className={labelClass}>Memory scope</Label>
      <p className="text-xs text-muted-foreground">
        How durable memory is keyed. Memory is hydrated into the container before the run and persisted after — placed after the
        cache-stable system prompt so it never invalidates the prompt cache.
      </p>
      <div className="space-y-2" role="radiogroup" aria-label="Memory scope">
        {options.map(option => (
          <label
            key={option.value}
            htmlFor={`agent-memory-${option.value}`}
            className={cn(
              'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors',
              config.memory === option.value ? 'border-foreground bg-muted/50' : 'border-border',
              disabled && 'cursor-not-allowed opacity-60',
            )}
          >
            <input
              id={`agent-memory-${option.value}`}
              type="radio"
              name="agent-memory-scope"
              value={option.value}
              checked={config.memory === option.value}
              onChange={() => onChange({ ...config, memory: option.value })}
              disabled={disabled}
              className="mt-1"
            />
            <span>
              <span className="block text-sm font-semibold text-foreground">{option.title}</span>
              <span className="block text-xs text-muted-foreground">{option.hint}</span>
            </span>
          </label>
        ))}
      </div>
    </div>
  )
}
